import { Placeholder, Text } from '@sitecore-jss/sitecore-jss-react';
import React, { useEffect, useLayoutEffect, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import {
  setNextPage,
  setPreventPageUpdate,
  setPrevPage,
  showErrors,
} from '../../../redux/slices/appStateSlice';
import ErrorMessage from '../../common/ErrorMessage';
import { useInternalNavigate } from '../../../utils/navigate/useNavigate';
import ReplaceableRichText from '../../../utils/text/ReplaceableRichText';
import ReplaceableText from '../../../utils/text/ReplaceableText';
import { errorMessages } from '../../../utils/errors/constants';

const blockingErrorTypes = new Set([
  errorMessages.EMPTY,
  errorMessages.TOO_FEW,
  errorMessages.IN_FLIGHT,
]);

const FormPage = ({
  fields,
  rendering,
  currentErrors,
  hasError,
  nextPage,
  interstitial,
  dependantInvalid,
}) => {
  const [enableSubmit, setEnableSubmit] = useState(false);
  const [callback, setCallback] = useState();
  const dispatch = useDispatch();
  const navigate = useInternalNavigate();
  const preventPageUpdate = useSelector(state => state.appState.preventPageUpdate);
  const disableButtonClass = enableSubmit ? '' : ' vds-button--disabled';

  const checkSubmit = () => {
    const hasEmptyError = currentErrors?.find(e => blockingErrorTypes.has(e.type));
    setEnableSubmit(!hasEmptyError);
  };

  const handleSubmit = e => {
    if (hasError || (callback && !callback())) {
      e.preventDefault();
      dispatch(showErrors());

      const firstInlineError = currentErrors?.find(error => error.inline);
      if (firstInlineError) {
        setTimeout(() => {
          const errorElement = document.querySelector(
            `[name="${firstInlineError.name}"], #${firstInlineError.name}, [data-errormessageorigin="${firstInlineError.name}"]`
          );
          if (errorElement) {
            errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
      }
      return false;
    }

    if (fields.submitButtonDispatchType?.value) {
      let payload = null;
      try {
        if (fields.submitButtonDispatchPayload?.value) {
          payload = JSON.parse(fields.submitButtonDispatchPayload.value);
        }
      } catch {
        payload = fields.submitButtonDispatchPayload.value;
      }
      const dispatchData = { type: fields.submitButtonDispatchType.value, payload };
      dispatch(dispatchData);
      return;
    }

    dispatch(setNextPage(null));
    dispatch(setPrevPage(null));
    navigate(nextPage);
  };

  useLayoutEffect(() => {
    if (preventPageUpdate) {
      dispatch(setPreventPageUpdate(false));
    } else {
      if (fields.nextPage?.value) {
        dispatch(setNextPage(fields.nextPage.value.href));
      }
      if (fields.prevPage?.value) {
        dispatch(setPrevPage(fields.prevPage.value.href));
      } else {
        dispatch(setPrevPage(null));
      }
    }

    checkSubmit();

    if (typeof window !== 'undefined') {
      window.scrollTo(0, 0);
    }
  }, [preventPageUpdate]);

  useEffect(() => {
    checkSubmit();
  }, [currentErrors]);

  useEffect(() => {
    if (dependantInvalid && hasError) {
      dispatch(showErrors());
    }
  }, [dependantInvalid, hasError]);

  return (
    <div className="quote-app-container">
      <div className="quote-app" style={{ display: interstitial ? 'none' : '' }}>
        <Placeholder name="formPage-navigation" rendering={rendering} />
        <div className="quote-app-container vds-main-content vds-d2c-form-page vds-scroll-check">
          <div className="vds-d2c-form-page__section">
            <div className="vds-d2c-form-page__inner">
              <Placeholder name="formPage-progress" rendering={rendering} />
            </div>
          </div>
          <div className="vds-d2c-form-page__section">
            <div className="vds-d2c-form-page__inner title-margin">
              <div className="vds-section-heading vds-section-heading--centred vds-section-heading--form">
                <div className="vds-section-heading__inner">
                  <ReplaceableText
                    tag="h1"
                    className="vds-section-heading__title"
                    field={fields.title}
                  />
                  <ReplaceableRichText field={fields.intro} isCurrency="false" />
                </div>
              </div>
            </div>
          </div>
          <div className="vds-d2c-form-page__section">
            <div className="vds-d2c-form-page__inner">
              <Placeholder
                name="formPage-questions"
                rendering={rendering}
                setCallback={setCallback}
              />
              <ErrorMessage />
              <div className="vds-form__group vds-form__group--d2c-actions">
                <button
                  className={`vds-button vds-button--fluid${disableButtonClass}`}
                  onClick={handleSubmit}
                  style={{ cursor: 'pointer', pointerEvents: 'unset' }}
                  data-testid={fields.testid?.value}
                >
                  <Text field={fields.submitButtonText} />
                </button>
              </div>
            </div>
          </div>
          <Placeholder name="formPage-footer" rendering={rendering} />
        </div>
      </div>
      <Placeholder name="formPage-overlays" rendering={rendering} />
    </div>
  );
};

const mapStateToProps = (state, props) => {
  return {
    ...props,
    currentErrors: state.appState?.errorMessages,
    hasError: state.appState?.hasError,
    nextPage: state.appState?.nextPage,
    interstitial: state.appState?.overlay?.interstitial,
    dependantInvalid: !!state.appState?.quoteRevalidation?.dependantInvalid,
  };
};

FormPage.propTypes = {
  fields: PropTypes.shape({
    title: PropTypes.shape({ value: PropTypes.string }),
    intro: PropTypes.shape({ value: PropTypes.string }),
    submitButtonText: PropTypes.shape({ value: PropTypes.string }),
    submitButtonDispatchType: PropTypes.shape({ value: PropTypes.string }),
    submitButtonDispatchPayload: PropTypes.shape({ value: PropTypes.string }),
    testid: PropTypes.shape({ value: PropTypes.string }),
    nextPage: PropTypes.shape({
      value: PropTypes.shape({ href: PropTypes.string }),
    }),
    prevPage: PropTypes.shape({
      value: PropTypes.shape({ href: PropTypes.string }),
    }),
  }),
  rendering: PropTypes.object,
  currentErrors: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string,
      name: PropTypes.string,
      inline: PropTypes.bool,
    })
  ),
  hasError: PropTypes.bool,
  nextPage: PropTypes.string,
  interstitial: PropTypes.bool,
  dependantInvalid: PropTypes.bool,
};

export default connect(mapStateToProps)(FormPage);
